import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router";
import jwtDecode from "jwt-decode";
import { RootState } from "../../store";
import EmployeeDetail from "../../views/administration/employees/employee-detail";
import Employees from "../../views/administration/employees/employees";
import ApprovalChains from "../../views/approval-chain/approval-chain";
import Benefits from "../../views/benefits/benefits";
import Dashboard from "../../views/dashboard/dashboard";
import FormNotifications from "../../views/forms/form-notifications";
import Forms from "../../views/forms/forms";
import ManageCatgories from "../../views/forms/manage-categories/manage-categories";
import ManageForms from "../../views/forms/manage-forms/manage-forms";
import Inventory from "../../views/inventory/inventory";
import Leave from "../../views/leave/leave";
import ForgetPassword from "../../views/login/forget-password/forget-password";
import Login from "../../views/login/Login";
import Register from "../../views/login/register/register";
import NotFound from "../components/not-found";
import PayHistory from "../../views/pay-history/pay-history";
import TimecardApproval from "../../views/timecard-approval/timecard-approval";
import Timecard from "../../views/timecard/timecard";
import DesktopLayout from "../layout/desktop/DesktopLayout";
import UserProfile from "../../views/user-profile/UserProfile";
import Configuration from "../../views/administration/configuration/configuration";
import Paycodes from "../../views/paycodes/paycode";
import getConfigs from "../../views/getConfigs";
import AccessDenied from "../components/access-denied";

const ValidateToken = ({ ...props }) => {
  const { children } = props;
  const { activeEmployee } = useSelector((state: RootState) => state.global);
  const location = useLocation();
  const configurations = getConfigs([
    "Dashboard",
    "PayHistory",
    "ShowTimecard",
    "Benefits",
    "Leave",
    "Forms",
    "Inventory",
    "ShowTimecard",
    "EnablePaycodeMapping",
    "ShowApprovalChain",
    "ShowTimecardApproval",
    "ManagersCanViewAcknowledgements",
    "ManagerCanAddForm",
    "ManagerCanAddCategory"
  ]);

  const storedToken = localStorage.getItem("token");
  let tokenData: any = {};
  if (storedToken) {
    tokenData = jwtDecode(storedToken);
  }
  const expiryTime = Number(tokenData.exp * 1000); // converted to milliseconds
  const isExpiryTimeNotLapsed = expiryTime > new Date().getTime();

  if (props.public && !storedToken) {
    return children;
  } else if (props.public && storedToken && isExpiryTimeNotLapsed) {
    return <Navigate to="/dashboard" state={{ from: location }} />;
  } else if (!props.public && !storedToken) {
    return <Navigate to="/login" state={{ from: location }} />;
  } else if (!props.public && storedToken && !isExpiryTimeNotLapsed) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  // if (!token) {
  //   // Redirect them to the /login page, but save the current location they were
  //   // trying to go to when they were redirected. This allows us to send them
  //   // along to that page after they login, which is a nicer user experience
  //   // than dropping them off on the home page.
  //   return <Navigate to="/login" state={{ from: location }} />;
  // }

  // Added this to restrict user from manual routing
  switch (location.pathname) {
    case "/dashboard":
      if (configurations.Dashboard?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/pay-history":
      if (configurations.PayHistory?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/benefits":
      if (configurations.Benefits?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/leave":
      if (configurations.Leave?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/forms":
      if (configurations.Forms?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/inventory":
      if (configurations.Inventory?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/timecard":
      if (configurations.ShowTimecard?.toLowerCase() === "false") {
        return <AccessDenied />;
      }
      break;
    case "/paycodes":
      if (configurations.EnablePaycodeMapping?.toLowerCase() === "false") {
        return <AccessDenied />;
      } else if (!activeEmployee?.isAdmin) {
        return <AccessDenied />;
      }
      break;
    case "/config":
      if (!activeEmployee?.isAdmin) {
        return <AccessDenied />;
      }
      break;
    case "/approval-chain":
      if (configurations.ShowApprovalChain?.toLowerCase() === "false") {
        return <AccessDenied />;
      } else if (!activeEmployee?.isAdmin) {
        return <AccessDenied />;
      }
      break;
    case "/timecard-approval":
      if (configurations.ShowTimecardApproval?.toLowerCase() === "false") {
        return <AccessDenied />;
      } else if (!activeEmployee?.isAdmin && !activeEmployee?.isManager && !activeEmployee?.isApprover) {
        return <AccessDenied />;
      }
      break;
    case "/forms/acknowledgements":
      if (!activeEmployee?.isAdmin && !(configurations.ManagersCanViewAcknowledgements?.toLowerCase() === "true" && activeEmployee?.isManager)) {
        return <AccessDenied />;
      }
      break;
    case "/forms/manage-forms":
      if (!activeEmployee?.isAdmin && !(configurations.ManagerCanAddForm?.toLowerCase() === "true" && activeEmployee?.isManager)) {
        return <AccessDenied />;
      }
      break;
    case "/forms/manage-categories":
      if (!activeEmployee?.isAdmin && !(configurations.ManagerCanAddForm?.toLowerCase() === "true" && activeEmployee?.isManager)) {
        return <AccessDenied />;
      }
      break;
    default:
      if (location.pathname.includes("/employees") && !activeEmployee?.isAdmin && !activeEmployee?.isManager) {
        return <AccessDenied />;
      }
  }

  return <DesktopLayout>{children}</DesktopLayout>;
};

const Router = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ValidateToken public>
            <Login />
          </ValidateToken>
        }
      />
      <Route
        path="/login"
        element={
          <ValidateToken public>
            <Login />
          </ValidateToken>
        }
      />
      <Route
        path="/register"
        element={
          <ValidateToken public>
            <Register />
          </ValidateToken>
        }
      />
      <Route
        path="/forget"
        element={
          <ValidateToken public>
            <ForgetPassword />
          </ValidateToken>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ValidateToken>
            <Dashboard />
          </ValidateToken>
        }
      />
      <Route
        path="/user-profile"
        element={
          <ValidateToken>
            <UserProfile />
          </ValidateToken>
        }
      />
      <Route
        path="/timecard"
        element={
          <ValidateToken>
            <Timecard />
          </ValidateToken>
        }
      />
      <Route
        path="/leave"
        element={
          <ValidateToken>
            <Leave />
          </ValidateToken>
        }
      />
      <Route
        path="/benefits"
        element={
          <ValidateToken>
            <Benefits />
          </ValidateToken>
        }
      />
      <Route
        path="/inventory"
        element={
          <ValidateToken>
            <Inventory />
          </ValidateToken>
        }
      />
      <Route
        path="/pay-history"
        element={
          <ValidateToken>
            <PayHistory />
          </ValidateToken>
        }
      />
      <Route
        path="/forms"
        element={
          <ValidateToken>
            <Forms />
          </ValidateToken>
        }
      />
      <Route
        path="/forms/manage-forms"
        element={
          <ValidateToken>
            <ManageForms />
          </ValidateToken>
        }
      />
      <Route
        path="/forms/acknowledgements"
        element={
          <ValidateToken>
            <FormNotifications />
          </ValidateToken>
        }
      />
      <Route
        path="/timecard-approval"
        element={
          <ValidateToken>
            <TimecardApproval />
          </ValidateToken>
        }
      />
      <Route
        path="/approval-chain"
        element={
          <ValidateToken>
            <ApprovalChains />
          </ValidateToken>
        }
      />
      <Route
        path="/employees"
        element={
          <ValidateToken>
            <Employees />
          </ValidateToken>
        }
      />
      <Route
        path="/config"
        element={
          <ValidateToken>
            <Configuration />
          </ValidateToken>
        }
      />
      <Route
        path="/employees/detail"
        element={
          <ValidateToken>
            <EmployeeDetail />
          </ValidateToken>
        }
      />
      <Route
        path="/forms/manage-categories"
        element={
          <ValidateToken>
            <ManageCatgories />
          </ValidateToken>
        }
      />
      <Route
        path="/paycodes"
        element={
          <ValidateToken>
            <Paycodes />
          </ValidateToken>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Router;
